'use client';

import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
// 商品项类型定义
interface GoodsItem {
  id: number;
  shop_sku: string;
  frame_color: string;
  image_url: string;
  is_glass: number;
  short_name: string;
  prescription_type: string;
  prescription_type_name?: string;
  prescription: any;
  lens_info?: any;
  prescriptions_image_url?: string;
}

// 选中商品类型
interface SelectedGoods {
  id: number;
  shop_sku: string;
}

interface GoodsSelectProps {
  open: boolean;
  onClose: () => void;
  onSelect: (goods: SelectedGoods[]) => void;
  selectedGoods: SelectedGoods[];
  skuList: GoodsItem[];
  loading?: boolean;
}

export default function GoodsSelect({
  open,
  onClose,
  onSelect,
  selectedGoods,
  skuList,
  loading = false,
}: GoodsSelectProps) {
  const t = useTranslations('contact-zeelool.GoodsSelect');
  const [localSelectedGoods, setLocalSelectedGoods] = useState<SelectedGoods[]>([]);

  // 同步外部选中状态
  useEffect(() => {
    setLocalSelectedGoods(selectedGoods);
  }, [selectedGoods]);

  // 处理商品选择
  const handleSelectProduct = (item: GoodsItem) => {
    const isSelected = localSelectedGoods.some((goods) => goods.id === item.id);

    if (isSelected) {
      // 取消选择
      setLocalSelectedGoods((prev) => prev.filter((goods) => goods.id !== item.id));
    } else {
      // 添加选择
      setLocalSelectedGoods((prev) => [
        ...prev,
        {
          id: item.id,
          shop_sku: item.shop_sku,
        },
      ]);
    }
  };

  // 确认选择
  const handleConfirmSelection = () => {
    onSelect(localSelectedGoods);
    onClose();
  };

  // 获取镜片名称（简化版本，对应Vue中的lensName函数）
  const getLensName = (lensInfo: any) => {
    if (!lensInfo) return '';
    // lens_display_type 是否为全视线镜片 1 不是 2 是
    if (lensInfo.lens_display_type === 2) {
      return lensInfo.brand_name ? lensInfo.brand_name : lensInfo.lens_name;
    }
    return lensInfo.lens_name || '';
  };

  // PC端内容
  const renderContentPc = () => {
    // 渲染商品列表的不同状态
    const renderGoodsList = () => {
      if (loading) {
        return (
          <div className="flex justify-center items-center py-20">
            <CircularProgress />
          </div>
        );
      }

      if (skuList.length === 0) {
        return (
          <div className="text-center my-26">
            <Image
              className="block mx-auto w-[53px] h-[66px]"
              src="/contact-zeelool/no-data.png"
              alt=""
              width={53}
              height={66}
            />
            <span className="block pt-15 leading-16 text-14 font-normal text-secondary-text">
              {t('noResult')}
            </span>
          </div>
        );
      }

      return (
        <div className="max-h-[60vh] w-full flex flex-col items-center overflow-auto">
          <div className="w-[680px]">
            {skuList.map((item) => {
              const isSelected = localSelectedGoods.some((goods) => goods.id === item.id);

              return (
                <div key={item.id} className="border-t border-[#eeeeee]">
                  <div
                    className={`
                  flex items-center justify-start h-[112px] w-full p-16 cursor-pointer
                  ${isSelected ? 'bg-[#f8f7ff]' : 'hover:bg-[#f8f7ff]'}
                `}
                    onClick={() => handleSelectProduct(item)}
                  >
                    {/* 商品图片 */}
                    <div className="flex items-center justify-center w-[80px] h-[80px] bg-[#eeeeee] rounded-6 overflow-hidden">
                      {item.is_glass === 1 ? (
                        <div className="flex items-center justify-center w-[80px] h-[80px]">
                          <Image
                            src={item.image_url}
                            alt={item.shop_sku}
                            className="w-[64px] h-[32px] mix-blend-multiply"
                            width={64}
                            height={32}
                          />
                        </div>
                      ) : (
                        <div className="w-[80px] h-[80px]">
                          <Image
                            src={item.image_url}
                            alt={item.shop_sku}
                            className="w-full h-full object-cover"
                            width={80}
                            height={80}
                          />
                        </div>
                      )}
                    </div>

                    {/* 商品基本信息 */}
                    <div className="ml-16 w-[225px]">
                      <div className="font-bb text-14 text-primary-text leading-16">
                        {item.shop_sku}
                      </div>
                      <div className="font-br text-14 text-primary-text leading-16 mt-8">
                        {item.frame_color}
                      </div>
                    </div>

                    {/* 镜片信息 */}
                    <div className="flex-1 w-[221px]">
                      <div className="font-bb text-14 text-primary-text">
                        {item?.prescription_type_name ?? item.prescription_type}
                      </div>
                      <div className="font-br text-14 text-primary-text leading-16 mt-8">
                        {getLensName(item.lens_info)}
                      </div>
                      {item.lens_info && (
                        <div className="font-br text-14 text-primary-text leading-16 mt-4">
                          {item.lens_info.lens_index}
                        </div>
                      )}
                    </div>

                    {/* 选中状态 */}
                    <div className="w-[24px] h-[24px]">
                      <Image
                        src={isSelected ? '/payment/select.svg' : '/payment/not-select.svg'}
                        alt="check"
                        width={20}
                        height={20}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="mt-10">
        {/* 商品列表容器 */}
        <div className="relative pb-[100px]">
          {renderGoodsList()}

          {/* 底部按钮 */}
          <div className="absolute bottom-0 left-0 w-full p-16 bg-white flex justify-center">
            <Button
              disabled={!localSelectedGoods.length}
              onClick={handleConfirmSelection}
              variant="contained"
              color="primary"
              sx={{
                width: '420px',
                height: '48px',
                fontSize: '16px',
                fontFamily: 'var(--font-bricolage-bold)',
                fontWeight: 'normal',
                color: '#ffffff',
                borderRadius: '6px',
              }}
            >
              {t('Select')}
              {localSelectedGoods.length > 0 && (
                <span className="ml-1">({localSelectedGoods.length})</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // 移动端内容（移动端和PC端结构基本相同，但样式可以稍作调整）
  const renderContentWap = () => {
    // 渲染移动端商品列表的不同状态
    const renderMobileGoodsList = () => {
      if (loading) {
        return (
          <div className="flex justify-center items-center py-20">
            <CircularProgress />
          </div>
        );
      }

      if (skuList.length === 0) {
        return (
          <div className="text-center py-60">
            <Image
              className="block mx-auto w-[53px] h-[66px]"
              src="/contact-zeelool/no-data.png"
              alt=""
              width={53}
              height={66}
            />
            <span className="block pt-15 leading-16 text-14 font-normal text-secondary-text">
              {t('noResult')}
            </span>
          </div>
        );
      }

      return (
        <div className="w-full h-full px-14 pt-12 overflow-auto">
          {skuList.map((item) => {
            const isSelected = localSelectedGoods.some((goods) => goods.id === item.id);

            return (
              <div key={item.id} className=" border-[#eeeeee] bg-white rounded-8">
                <div
                  className="flex items-center justify-start h-[112px] w-full p-16 cursor-pointer"
                  onClick={() => handleSelectProduct(item)}
                >
                  {/* 商品图片 */}
                  <div className="flex items-center justify-center w-[80px] h-[80px]  bg-[#eeeeee] rounded-[12px] overflow-hidden">
                    {item.is_glass === 1 ? (
                      <div className="flex items-center justify-center w-[80px] h-[80px]">
                        <Image
                          src={item.image_url}
                          alt={item.shop_sku}
                          className="w-[64px] h-[32px] mix-blend-multiply"
                          width={64}
                          height={32}
                        />
                      </div>
                    ) : (
                      <div className="w-[80px] h-[80px]">
                        <Image
                          src={item.image_url}
                          alt={item.shop_sku}
                          className="w-full h-full object-cover"
                          width={80}
                          height={80}
                        />
                      </div>
                    )}
                  </div>

                  {/* 商品基本信息 */}
                  <div className="ml-16  flex-1">
                    <div className="font-bb text-12 text-primary-text leading-16">
                      {item.short_name}
                    </div>
                    <div className="font-br text-12 text-primary-text leading-16 mt-4">
                      {t('Color')}: {item.frame_color}
                    </div>
                    {item.prescription_type && (
                      <div className="font-br text-12 text-primary-text mt-4">
                        {t('Rx')}: {item?.prescription_type_name ?? item.prescription_type}
                      </div>
                    )}
                    {item.lens_info && (
                      <div className="font-br text-12 text-primary-text leading-16 mt-4">
                        {item.lens_info.lens_index}
                      </div>
                    )}
                  </div>

                  {/* 选中状态 */}
                  <div className="w-[24px] h-[24px]">
                    <Image
                      src={isSelected ? '/payment/select.svg' : '/payment/not-select.svg'}
                      alt="check"
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="h-full flex flex-col">
        {/* 商品列表 */}
        <div className="flex-1 overflow-hidden">{renderMobileGoodsList()}</div>

        {/* 底部按钮 */}
        <div className="p-16 bg-white border-t border-gray-200">
          <Button
            disabled={!localSelectedGoods.length}
            onClick={handleConfirmSelection}
            fullWidth
            variant="contained"
            color="primary"
            sx={{
              height: '48px',
              fontSize: '16px',
              fontFamily: 'var(--font-bricolage-bold)',
              fontWeight: 'normal',
              borderRadius: '20px',
            }}
          >
            {t('Select')}
            {localSelectedGoods.length > 0 && (
              <span className="ml-1">({localSelectedGoods.length})</span>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <WebOnly>
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          title={t('title')}
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: '728px',
              borderRadius: '8px',
            },
            '& .MuiDialogContent-root': {
              padding: '0',
            },
          }}
          footer={null}
        >
          {renderContentPc()}
        </Dialog>
      </WebOnly>

      <WapOnly>
        <Popup
          open={open}
          onClose={onClose}
          position="bottom"
          height="80vh"
          title={t('title')}
          className="rounded-t-20"
          sx={{
            '& .MuiBox-root': {
              padding: '0',
              backgroundColor: '#f5f5f5',
            },
            '& .popup-title': {
              padding: '16px 50px',
            },
          }}
        >
          {renderContentWap()}
        </Popup>
      </WapOnly>
    </>
  );
}
